import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Close, Search, Task, User, UserMultiple } from '@carbon/icons-react';
import { HeaderGlobalAction } from '@carbon/react';
import axiosInstance from '../utils/axiosInstance';
import './GlobalSearch.css';

// Pretraga startuje tek od ove dužine unosa (izbegava bespotrebne pozive).
const MIN_QUERY_LENGTH = 2;
// Maksimalna dužina pojma pretrage koju backend prihvata.
const MAX_QUERY_LENGTH = 100;
// Pauza posle kucanja pre nego što se pozove backend (debounce, 250-300ms).
const DEBOUNCE_MS = 300;
// Maksimalan broj rezultata PO GRUPI (backend dozvoljava 1-25, default 10).
const RESULT_LIMIT = 10;

// Ikonica po tipu grupe koju backend vraća.
const GROUP_ICONS = {
  resident: User,
  employee: UserMultiple,
  task: Task,
};

// Fallback ruta ako backend ne pošalje `route` u rezultatu.
const ROUTE_BUILDERS = {
  resident: (id) => `/residents/${id}`,
  employee: (id) => `/employees/${id}`,
  task: (id) => `/tasks/${id}`,
};

// Normalizuje grupu iz odgovora GET /api/search u oblik koji UI očekuje.
const normalizeGroup = (group) => {
  const type = group?.type;
  const buildRoute = ROUTE_BUILDERS[type];
  const items = Array.isArray(group?.items) ? group.items : [];
  return {
    key: type || group?.label || 'group',
    label: group?.label || type || '',
    icon: GROUP_ICONS[type] || Search,
    items: items
      .filter((item) => item && item.id != null)
      .map((item) => ({
        id: item.id,
        title: item.title || '',
        subtitle: item.subtitle || '',
        imageUrl: item.imageUrl || null,
        to: item.route || (buildRoute ? buildRoute(item.id) : '/'),
      })),
  };
};

/**
 * Opšta (globalna) pretraga u zaglavlju.
 *
 * Kuca se pojam (min 2 znaka) i poziva se GET /api/search koji vraća rezultate
 * grupisane po tipu (štićenici, zaposleni, zadaci). Grupe zavise od dozvola
 * korisnika i određuje ih backend. Klik na rezultat vodi na odgovarajuću rutu.
 */
const GlobalSearch = () => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Mobilni prikaz: polje je sakriveno, a ikonica otvara klizeću formu.
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 671px)').matches
  );

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const showPanel = !isMobileView && isFocused && hasQuery;
  const showMobilePanel = isMobileView && isMobileOpen && hasQuery;

  // Prati da li smo na mobilnom prikazu; pri prelasku na desktop zatvori formu.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 671px)');
    const handleChange = (event) => {
      setIsMobileView(event.matches);
      if (!event.matches) setIsMobileOpen(false);
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Kada se mobilna forma otvori, fokusiraj polje (posle početka animacije).
  useEffect(() => {
    if (!isMobileOpen) return undefined;
    const timer = setTimeout(() => mobileInputRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [isMobileOpen]);

  // Pretraga preko jedinstvenog /api/search endpointa, sa debounce-om i
  // otkazivanjem prethodnog zahteva (rezultati ne stižu van redosleda).
  useEffect(() => {
    const term = query.trim();

    if (term.length < MIN_QUERY_LENGTH) {
      setGroups([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('/api/search', {
          params: { q: term, limit: RESULT_LIMIT },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        const rawGroups = response.data?.data?.groups;
        const nextGroups = Array.isArray(rawGroups)
          ? rawGroups
              .map(normalizeGroup)
              .filter((group) => group.items.length > 0)
          : [];
        setGroups(nextGroups);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Greška pri opštoj pretrazi:', err);
        setError(
          err.response?.status === 429
            ? 'Previše zahteva. Pokušajte ponovo za koji trenutak.'
            : 'Pretraga trenutno nije moguća.'
        );
        setGroups([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Ravna lista svih rezultata (za navigaciju tastaturom) + grupe sa indeksima.
  const { flatItems, indexedGroups } = useMemo(() => {
    const flat = [];
    const indexed = groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        const index = flat.length;
        flat.push({ ...item, groupKey: group.key });
        return { ...item, index };
      }),
    }));
    return { flatItems: flat, indexedGroups: indexed };
  }, [groups]);

  // Novi set rezultata -> poništi prethodnu selekciju.
  useEffect(() => {
    setActiveIndex(-1);
  }, [groups]);

  // Klik van pretrage zatvara panel (desktop) / klizeću formu (mobilni).
  useEffect(() => {
    if (!isFocused && !isMobileOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      const insideForm = containerRef.current?.contains(target);
      const onToggle =
        target instanceof Element &&
        Boolean(target.closest('.global-search__toggle'));
      if (!insideForm && !onToggle) {
        setIsFocused(false);
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isFocused, isMobileOpen]);

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;
      navigate(item.to);
      setQuery('');
      setGroups([]);
      setActiveIndex(-1);
      setIsFocused(false);
      setIsMobileOpen(false);
      inputRef.current?.blur();
      mobileInputRef.current?.blur();
    },
    [navigate]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSelect(flatItems[activeIndex] || flatItems[0]);
  };

  const handleClear = () => {
    setQuery('');
    setGroups([]);
    setActiveIndex(-1);
    (isMobileView ? mobileInputRef : inputRef).current?.focus();
  };

  const openMobileSearch = () => setIsMobileOpen(true);

  const closeMobileSearch = () => {
    setIsMobileOpen(false);
    setQuery('');
    setGroups([]);
    setActiveIndex(-1);
    setIsFocused(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (isMobileView && isMobileOpen) {
        closeMobileSearch();
      } else {
        setIsFocused(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
      }
      return;
    }

    if (!showPanel && !showMobilePanel) return;
    if (flatItems.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
    }
  };

  return (
    <>
      {/* Mobile: ikonica pretrage kao zaseban flex element, tik uz dugme
          obaveštenja (isti nivo i ista visina kao to dugme). */}
      {isMobileView && (
        <HeaderGlobalAction
          aria-label="Pretraži"
          tooltipAlignment="end"
          className="global-search__toggle"
          isActive={isMobileOpen}
          onClick={openMobileSearch}
        >
          <Search size={24} />
        </HeaderGlobalAction>
      )}

      <form
        className={`global-search ${isMobileOpen ? 'is-mobile-open' : ''}`.trim()}
        role="search"
        ref={containerRef}
        onSubmit={handleSubmit}
      >
      {/* Desktop: inline polje pretrage */}
      {!isMobileView && (
        <div className={`global-search__field ${isFocused ? 'is-focused' : ''}`}>
          <Search size={16} className="global-search__icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            className="global-search__input"
            placeholder="Pretraži..."
            aria-label="Opšta pretraga"
            autoComplete="off"
            role="combobox"
            aria-expanded={showPanel}
            aria-controls="global-search-results"
            maxLength={MAX_QUERY_LENGTH}
            aria-autocomplete="list"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
          />
          {query && (
            <button
              type="button"
              className="global-search__clear"
              aria-label="Obriši pretragu"
              onClick={handleClear}
            >
              <Close size={16} />
            </button>
          )}
        </div>
      )}

      {/* Mobile: forma koja ulazi s desna na levo */}
      {isMobileView && (
        <div className={`global-search__mobile-bar ${isMobileOpen ? 'is-open' : ''}`}>
          <div className={`global-search__mobile-field ${isFocused ? 'is-focused' : ''}`}>
            <Search size={16} className="global-search__icon" aria-hidden="true" />
            <input
              ref={mobileInputRef}
              type="search"
              className="global-search__input"
              placeholder="Pretraži..."
              aria-label="Opšta pretraga"
              autoComplete="off"
              role="combobox"
              aria-expanded={showMobilePanel}
              aria-controls="global-search-results"
              maxLength={MAX_QUERY_LENGTH}
              aria-autocomplete="list"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => setIsFocused(true)}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <button
                type="button"
                className="global-search__clear"
                aria-label="Obriši pretragu"
                onClick={handleClear}
              >
                <Close size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="global-search__mobile-close"
            aria-label="Zatvori pretragu"
            onClick={closeMobileSearch}
          >
            <Close size={20} />
          </button>
        </div>
      )}

      {(showPanel || showMobilePanel) && (
        <div className="global-search__panel" id="global-search-results" role="listbox">
          {loading && <div className="global-search__state">Pretraga u toku…</div>}

          {!loading && error && (
            <div className="global-search__state global-search__state--error">{error}</div>
          )}

          {!loading && !error && flatItems.length === 0 && (
            <div className="global-search__state">
              Nema rezultata za „{trimmedQuery}”.
            </div>
          )}

          {!loading &&
            !error &&
            indexedGroups.map((group) => (
              <div className="global-search__group" key={group.key}>
                <div className="global-search__group-title">{group.label}</div>
                {group.items.map((item) => {
                  const Icon = group.icon;
                  return (
                    <button
                      type="button"
                      key={`${group.key}-${item.id}`}
                      className={`global-search__item ${
                        item.index === activeIndex ? 'is-active' : ''
                      }`}
                      role="option"
                      aria-selected={item.index === activeIndex}
                      onMouseEnter={() => setActiveIndex(item.index)}
                      onClick={() => handleSelect(item)}
                    >
                      <span className="global-search__item-media" aria-hidden="true">
                        <Icon size={16} className="global-search__item-icon" />
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="global-search__item-avatar"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </span>
                      <span className="global-search__item-text">
                        <span className="global-search__item-title">{item.title}</span>
                        {item.subtitle && (
                          <span className="global-search__item-subtitle">{item.subtitle}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
        </div>
      )}
      </form>
    </>
  );
};

export default GlobalSearch;

